import { useContext } from 'react'
import { useAppSelector } from '@/redux/hooks'
import {
  BrandingOverride,
  BrandingOverrideContext,
  PreviewDevice,
  PreviewDeviceContext
} from './preview-contexts'

// The branding the preview draws with: an editor's unsaved values when one is provided,
// otherwise what is stored. Mirrors usePortalTheme, for the attributes that do not fit the
// design-system theme shape.
export function usePortalBranding(): BrandingOverride {
  const override = useContext(BrandingOverrideContext)
  const theme = useAppSelector(
    (store) => store.settingsApi.settings.response.theme
  )

  if (override) return override

  return {
    backgroundTextColor: theme?.background_text_color,
    logoHeight: {
      desktop: theme?.logo_height?.desktop,
      mobile: theme?.logo_height?.mobile
    },
    title: theme?.title,
    logoUrl: theme?.logo_url,
    backgroundImageUrl: theme?.background_image_url
  }
}

// The url the preview draws the logo from: a picked-but-unsaved image when there is one, the
// stored one otherwise. The override carries only what the editor has changed, so an editor that
// has touched no image still needs the stored value underneath it.
export function usePortalLogoUrl(): string | undefined {
  return useStoredOrOverridden('logo_url', (o) => o.logoUrl)
}

export function usePortalBackgroundImageUrl(): string | undefined {
  return useStoredOrOverridden(
    'background_image_url',
    (o) => o.backgroundImageUrl
  )
}

function useStoredOrOverridden(
  stored: 'logo_url' | 'background_image_url',
  pick: (override: BrandingOverride) => string | undefined
): string | undefined {
  const override = useContext(BrandingOverrideContext)
  const theme = useAppSelector(
    (store) => store.settingsApi.settings.response.theme
  )

  return (override && pick(override)) || theme?.[stored]
}

export function usePreviewDevice(): PreviewDevice {
  return useContext(PreviewDeviceContext)
}

// The portal sizes the logo h-[--logo-mobile-height] and swaps to the desktop value from md: up,
// so which one the preview draws follows the device it is standing in for. The fallbacks are the
// portal's own defaults, which is what a shopper sees while the attribute is unset — and the
// simplified layout defaults to a larger logo than the other two, so they are per caller.
export function usePortalLogoHeight(
  fallback: { mobile: string; desktop: string } = {
    mobile: '1.5rem',
    desktop: '2rem'
  }
): string {
  const { logoHeight } = usePortalBranding()
  const device = usePreviewDevice()

  return device === 'desktop'
    ? logoHeight?.desktop || fallback.desktop
    : logoHeight?.mobile || fallback.mobile
}
